import { useEffect, useState } from 'react';
import Pagination from '../../components/molecules/pagination';
import { ExhibitCard } from '../../features/exhibits/components/exhibit-card';
import ExhibitFilters from '../../features/exhibits/components/exhibit-filters';
import { useExhibits } from '../../hooks/use-exhibits';
import { SearchFilters } from '../../types/models';
import { useTranslation } from 'react-i18next'; // Import useTranslation

/**
 * Exhibit list page component
 *
 * Features:
 * - Displays a grid of exhibit cards
 * - Provides filtering and search functionality
 * - Includes pagination for navigating through results
 * - Handles loading and empty states
 */
const ExhibitList = () => {
    // Default page size
    const PAGE_SIZE = 12;
    const { t } = useTranslation(); // Initialize translation hook

    // Initialize filters state
    const [filters, setFilters] = useState<SearchFilters>({
        per_page: PAGE_SIZE,
        page: 1,
        sort_by: 'created_at',
        sort_direction: 'desc',
    });

    // Use the exhibits hook to fetch and manage data
    const { exhibits, isLoading, error, totalCount, currentPage, lastPage, fetchExhibits } = useExhibits();

    // Fetch exhibits whenever filters change
    useEffect(() => {
        fetchExhibits(filters);
    }, [filters.page, filters.per_page, filters.query, filters.status, filters.sort_by, filters.sort_direction]);

    // Handle filter changes
    const handleFilterChange = (newFilters: SearchFilters) => {
        setFilters((prevFilters) => {
            // Only update if values actually changed
            const changed = Object.keys(newFilters).some((key) => prevFilters[key as keyof SearchFilters] !== newFilters[key as keyof SearchFilters]);
            return changed
                ? {
                      ...prevFilters,
                      ...newFilters,
                      page: 1, // Reset to page 1 when filters change
                  }
                : prevFilters;
        });
    };

    // Handle page change
    const handlePageChange = (page: number) => {
        setFilters((prevFilters) => ({
            ...prevFilters,
            page,
        }));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Handle items per page change
    const handleItemsPerPageChange = (perPage: number) => {
        setFilters((prevFilters) => ({
            ...prevFilters,
            per_page: perPage,
            page: 1, // Reset to page 1 when changing items per page
        }));
    };

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="flex flex-col gap-8">
                {/* Page header */}
                <div className="flex flex-col gap-2">
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{t('exhibits.title', 'Exhibits')}</h1>
                    <p className="text-gray-600 dark:text-gray-300">{t('exhibits.description', "Explore the museum's collection of exhibits and artifacts")}</p>
                </div>

                {/* Filters */}
                <ExhibitFilters
                    onFiltersChange={handleFilterChange}
                    initialFilters={{
                        query: filters.query,
                        sort_by: filters.sort_by,
                        sort_direction: filters.sort_direction,
                        status: filters.status,
                        // Exclude page and per_page
                    }}
                    className="mb-2"
                />

                {/* Loading state */}
                {isLoading && (
                    <div className="flex items-center justify-center py-12">
                        <div className="border-primary h-12 w-12 animate-spin rounded-full border-b-2"></div>
                    </div>
                )}

                {/* Error state */}
                {error && !isLoading && (
                    <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-200">
                        <p className="font-medium">{t('exhibits.errorLoadingTitle', 'Error loading exhibits')}</p>
                        <p className="mt-1 text-sm">{error.message}</p> {/* Keep error.message as is */}
                    </div>
                )}

                {/* Empty state */}
                {!isLoading && !error && exhibits.length === 0 && (
                    <div className="rounded-lg bg-gray-50 p-12 text-center dark:bg-gray-800">
                        <div className="mx-auto mb-4 h-16 w-16 text-gray-400">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="M4 22h16a2 2 0 0 0 2-2V7.5L14.5 2H6a2 2 0 0 0-2 2v4"></path>
                                <polyline points="14 2 14 8 20 8"></polyline>
                                <path d="M3 15h6"></path>
                                <path d="M6 18V12"></path>
                            </svg>
                        </div>
                        <h3 className="mb-1 text-lg font-medium text-gray-900 dark:text-white">{t('exhibits.emptyStateTitle', 'No exhibits found')}</h3>
                        <p className="text-gray-500 dark:text-gray-400">
                            {filters.query || filters.status
                                ? t('exhibits.emptyStateDescriptionFiltered', "Try adjusting your filters to find what you're looking for.")
                                : t('exhibits.emptyStateDescriptionNoFilters', 'No exhibits have been added to the museum yet.')}
                        </p>
                    </div>
                )}

                {/* Exhibits grid */}
                {!isLoading && !error && exhibits.length > 0 && (
                    <>
                        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                            {exhibits.map((exhibit) => (
                                <ExhibitCard key={exhibit.id} exhibit={exhibit} /> // Pass the full exhibit object
                            ))}
                        </div>

                        {/* Pagination */}
                        {lastPage > 1 && (
                            <Pagination
                                currentPage={currentPage}
                                totalPages={lastPage}
                                onPageChange={handlePageChange}
                                itemsPerPage={filters.per_page}
                                onItemsPerPageChange={handleItemsPerPageChange}
                                itemsPerPageOptions={[12, 24, 48, 96]}
                                totalItems={totalCount}
                                className="mt-6"
                            />
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default ExhibitList;
